import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import './chartSetup';
import { CHART_FONT, NUMBER_FONT, formatAxisNumber, lockAxisWidth } from './chartSetup';
import { alertBandsPlugin, thresholdLabelsPlugin } from './chartPlugins';
import { waterFromMm, waterUnit } from './units';
const PLUGINS = [alertBandsPlugin, thresholdLabelsPlugin];
const AXIS_FONT = { family: CHART_FONT, size: 12 };
const TICK_FONT = { family: NUMBER_FONT, size: 11 };

const line = (label, data, extra) => ({
  type: 'line',
  label,
  data,
  borderWidth: 2,
  pointRadius: 0,
  pointHoverRadius: 3,
  tension: 0.15,
  spanGaps: false,
  ...extra,
});

const LocationChart = ({
  water,
  bands,
  start,
  end,
  timeUnit,
  yellowMm,
  redMm,
  unitSystem,
  alertSource = 'rule',
}) => {
  const drawnBands = useMemo(() => (bands || []).filter((band) => !band.hold), [bands]);
  const yWatch = yellowMm > 0 ? waterFromMm(yellowMm, unitSystem) : null;
  const yFlood = redMm > 0 ? waterFromMm(redMm, unitSystem) : null;
  const toY = (mm) => (mm == null ? null : waterFromMm(mm, unitSystem));
  const unit = waterUnit(unitSystem);

  const data = useMemo(() => {
    const datasets = [];
    const threshold = (label, y, color) => line(label, [
      { x: start, y },
      { x: end, y },
    ], {
      borderColor: color,
      borderDash: [6, 4],
      pointHoverRadius: 0,
      tension: 0,
      excludeFromTooltip: true,
    });
    if (yWatch != null) datasets.push(threshold('Watch', yWatch, '#8A6A12'));
    if (yFlood != null) datasets.push(threshold('Flood', yFlood, '#B3261E'));
    datasets.push(line(
      'Water level',
      (water || []).map((point) => ({ x: point.t, y: toY(point.mm) })),
      { borderColor: '#1F4E79' }
    ));
    return { datasets };
    // toY closes over unitSystem, which is a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [water, start, end, yWatch, yFlood, unitSystem]);

  const peak = useMemo(() => {
    const values = [
      ...(water || []).map((point) => point.mm),
      yellowMm,
      redMm,
      50,
    ].filter((value) => value != null && Number.isFinite(value));
    return Math.max(...values);
  }, [water, yellowMm, redMm]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'nearestPerDataset', intersect: false },
    plugins: {
      legend: { display: false },
      alertBands: { bands: drawnBands },
      thresholdLabels: {
        lines: [
          yWatch != null && { y: yWatch, label: 'Watch' },
          yFlood != null && { y: yFlood, label: 'Flood' },
        ].filter(Boolean),
      },
      tooltip: {
        mode: 'nearestPerDataset',
        intersect: false,
        filter: (item) => item.parsed.y != null,
        callbacks: {
          title: (items) => (
            items.length
              ? new Date(items[0].parsed.x).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
              : ''
          ),
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} ${unit}`,
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        min: start,
        max: end,
        time: {
          unit: timeUnit || 'hour',
          displayFormats: { hour: 'HH:mm', day: 'MMM d', month: 'MMM yyyy' },
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
          font: AXIS_FONT,
          color: '#3F4441',
        },
        grid: { color: '#E6E8E4' },
      },
      y: {
        min: 0,
        suggestedMax: waterFromMm(peak * 1.1, unitSystem),
        title: { display: true, text: unit, font: AXIS_FONT, color: '#3F4441' },
        ticks: {
          font: TICK_FONT,
          color: '#3F4441',
          callback: (value) => formatAxisNumber(value),
        },
        grid: { color: '#E6E8E4' },
        afterFit: lockAxisWidth,
      },
    },
  }), [drawnBands, end, peak, start, timeUnit, unit, unitSystem, yFlood, yWatch]);

  return (
    <>
      <div className="chart-plot chart-plot-water">
        <Chart
          type="line"
          data={data}
          options={options}
          plugins={PLUGINS}
          aria-label="Water level"
        />
      </div>
      <ul className="series-legend">
        <li><span className="swatch swatch-water" />Water level</li>
        <li><span className="swatch swatch-watch" />Watch</li>
        <li><span className="swatch swatch-flood" />Flood</li>
        {drawnBands.some((band) => band.level === 'yellow') && (
          <li><span className="swatch swatch-band swatch-band-yellow" />Forecast: rain rule yellow</li>
        )}
        {drawnBands.some((band) => band.level === 'red') && (
          <li>
            <span className="swatch swatch-band swatch-band-red" />
            {alertSource === 'rf' ? 'Forecast: AI model, flooding likely' : 'Forecast: rain rule red'}
          </li>
        )}
      </ul>
    </>
  );
};

export default LocationChart;
